// Deck construction (101).
//
// Builds a legal Duel deck around a Champion Legend: the legend fixes the Domain
// Identity, and everything else has to fit inside it. Used to generate opponents
// and to give a new player something playable without a deck builder.

import { CardIndex, fitsIdentity } from './cards.ts';
import type { ScriptIndex } from './effects.ts';
import type { DeckList } from './engine.ts';
import { nextInt, shuffle } from './rng.ts';
import type { CardDef, Domain } from './types.ts';

export interface DeckOptions {
  /** 103.2. At least 40 Main Deck cards. */
  mainDeckSize?: number;
  /** 103.3.a. Exactly 12 runes. */
  runeCount?: number;
  /** 103.2.b. Up to 3 copies of a named card. */
  maxCopies?: number;
  /**
   * Prefer cards whose text the engine fully models. A deck built from cards
   * with uncompiled text still plays, but half its cards would be doing nothing
   * they say they do, which makes for a poor game and a misleading test.
   */
  preferModelled?: boolean;
  scripts?: ScriptIndex;
}

/**
 * 164.1. Every set prints the six basic runes, so the id is looked up from the
 * pool rather than hard-coded; a set rotating out should not break deckbuilding.
 */
function basicRunes(ctx: BuildContext): Partial<Record<Domain, string>> {
  const out: Partial<Record<Domain, string>> = {};
  for (const c of ctx.playable) {
    if (c.type !== 'rune' || c.domains.length !== 1) continue;
    const domain = c.domains[0]!;
    if (!out[domain]) out[domain] = c.id;
  }
  return out;
}

export interface BuildContext {
  index: CardIndex;
  /** Cards that have artwork on disk and may appear in a deck. */
  playable: CardDef[];
}

export function buildContext(index: CardIndex, availableIds: ReadonlySet<string>): BuildContext {
  return {
    index,
    playable: index.filter((c) => availableIds.has(c.id)),
  };
}

/**
 * 103.1. Champion Legends that can actually anchor a deck.
 *
 * The test is the tag, not the supertype. Riot's data marks `superType:
 * champion` on only 9 of the 127 legends, which is a gap in the data rather
 * than a rule -- every legend in the game names a champion and every one of
 * them pairs with a printed champion unit. What actually has to hold is
 * 103.2.a.2: some champion unit shares the legend's tag, because that unit goes
 * in the Champion Zone.
 */
export function legalLegends(ctx: BuildContext): CardDef[] {
  const champTags = new Set<string>();
  for (const c of ctx.playable) {
    if (c.type !== 'champion') continue;
    for (const tag of c.tags) champTags.add(tag);
  }
  return ctx.playable.filter((c) =>
    c.type === 'legend' && c.tags.some((tag) => champTags.has(tag)));
}

/** The champion units that may be a given legend's Chosen Champion. */
export function championsFor(ctx: BuildContext, legend: CardDef): CardDef[] {
  const wanted = new Set(legend.tags);
  return ctx.playable.filter((c) =>
    c.type === 'champion' && c.tags.some((tag) => wanted.has(tag)));
}

/**
 * Builds a deck around `legend`. Curve-aware rather than random: a deck of
 * nothing but seven-drops technically satisfies every construction rule and
 * produces a game where nobody does anything for six turns.
 */
export function buildDeck(
  ctx: BuildContext, rng: { seed: number }, legend: CardDef, opts: DeckOptions = {},
): DeckList {
  const mainSize = opts.mainDeckSize ?? 40;
  const runeCount = opts.runeCount ?? 12;
  const maxCopies = opts.maxCopies ?? 3;
  const identity = legend.domains;

  const modelled = (c: CardDef): boolean => {
    if (!opts.preferModelled || !opts.scripts) return true;
    const s = opts.scripts[c.id];
    return !s || s.complete;
  };

  const legal = ctx.playable.filter((c) =>
    fitsIdentity(c, identity)
    && c.type !== 'legend' && c.type !== 'battlefield' && c.type !== 'rune'
    && c.type !== 'token' && c.superType !== 'token');

  // 103.2.a.2. The Chosen Champion must share the legend's champion tag, and
  // must itself fit the Domain Identity.
  const wantedTags = new Set(legend.tags);
  const champions = legal.filter((c) =>
    c.type === 'champion' && c.tags.some((tag) => wantedTags.has(tag)));
  const chosen = champions[nextInt(rng, Math.max(1, champions.length))]
    ?? legal.find((c) => c.type === 'champion');
  if (!chosen) throw new Error(`no champion available for ${legend.name}`);

  // 103.2.d. Signature cards only if they carry the legend's champion tag.
  const pool = legal.filter((c) =>
    c.superType !== 'signature' || c.tags.some((tag) => wantedTags.has(tag)));

  const preferred = pool.filter(modelled);
  const source = preferred.length >= 20 ? preferred : pool;

  // Aim for a playable curve rather than a uniform sample.
  const buckets: Record<string, CardDef[]> = { cheap: [], mid: [], top: [] };
  for (const c of source) {
    const total = c.cost.energy + c.cost.power;
    const key = total <= 2 ? 'cheap' : total <= 4 ? 'mid' : 'top';
    buckets[key]!.push(c);
  }
  const quota: Record<string, number> = {
    cheap: Math.round(mainSize * 0.4),
    mid: Math.round(mainSize * 0.4),
    top: mainSize - Math.round(mainSize * 0.4) * 2,
  };

  const counts = new Map<string, number>();
  const main: string[] = [];

  // 103.2.b. "Your deck can have any number of cards named Spiderling" lifts
  // the copy cap for that card. A deckbuilding rule, so this is the only place
  // that reads it -- the engine never asks.
  const unlimited = (card: CardDef): boolean => {
    for (const ability of opts.scripts?.[card.id]?.abilities ?? []) {
      if (ability.kind !== 'passive') continue;
      if (ability.effects.some((e) => e.p === 'unlimitedCopies')) return true;
    }
    return false;
  };

  const add = (card: CardDef): boolean => {
    const used = counts.get(card.name) ?? 0;
    const cap = card.keywords.includes('unique') ? 1
      : unlimited(card) ? Number.MAX_SAFE_INTEGER : maxCopies;
    if (used >= cap) return false;
    counts.set(card.name, used + 1);
    main.push(card.id);
    return true;
  };

  // The Chosen Champion's own copy occupies a Main Deck slot (103.2.a).
  add(chosen);

  for (const key of ['cheap', 'mid', 'top']) {
    const bucket = shuffle(rng, [...buckets[key]!]);
    let placed = key === 'cheap' ? 1 : 0; // the champion already counted
    let guard = 0;
    while (placed < quota[key]! && bucket.length > 0 && guard++ < mainSize * 20) {
      const card = bucket[nextInt(rng, bucket.length)]!;
      if (add(card)) placed++;
    }
  }

  // Top up from anywhere legal if the buckets ran dry.
  let guard = 0;
  const all = shuffle(rng, [...source]);
  while (main.length < mainSize && guard++ < mainSize * 40) {
    const card = all[nextInt(rng, all.length)];
    if (card) add(card);
  }

  // 103.3.a.1. Runes must match the identity; basics are split evenly.
  const basics = basicRunes(ctx);
  const runes: string[] = [];
  for (let i = 0; i < runeCount; i++) {
    const domain = identity[i % identity.length] ?? 'fury';
    const rune = basics[domain];
    if (!rune) throw new Error(`no basic rune for the ${domain} domain`);
    runes.push(rune);
  }

  // 103.4. One battlefield is used per player in a Duel (485.4).
  const battlefields = ctx.playable.filter((c) => c.type === 'battlefield');
  const battlefield = battlefields[nextInt(rng, Math.max(1, battlefields.length))];
  if (!battlefield) throw new Error('no battlefields available');

  return {
    name: legend.name,
    legend: legend.id,
    chosenChampion: chosen.id,
    battlefield: battlefield.id,
    main,
    runes,
  };
}

/** Sanity check against the construction rules, for tests and a deck editor. */
export function validateDeck(ctx: BuildContext, deck: DeckList): string[] {
  const problems: string[] = [];
  const legend = ctx.index.get(deck.legend);
  const identity = legend.domains;

  if (legend.type !== 'legend') problems.push('legend slot is not a legend');
  if (deck.main.length < 40) problems.push(`main deck has ${deck.main.length}, needs 40`);
  if (deck.runes.length !== 12) problems.push(`rune deck has ${deck.runes.length}, needs 12`);

  const champion = ctx.index.get(deck.chosenChampion);
  if (champion.type !== 'champion') problems.push('chosen champion is not a champion unit');
  if (!champion.tags.some((tag) => legend.tags.includes(tag))) {
    problems.push(
      `champion tags [${champion.tags}] share nothing with legend tags [${legend.tags}]`,
    );
  }

  const counts = new Map<string, number>();
  for (const id of deck.main) {
    const card = ctx.index.get(id);
    if (!fitsIdentity(card, identity)) {
      problems.push(`${card.name} is outside the domain identity`);
    }
    const n = (counts.get(card.name) ?? 0) + 1;
    counts.set(card.name, n);
    const cap = card.keywords.includes('unique') ? 1 : 3;
    if (n > cap) problems.push(`${card.name} appears ${n} times, cap is ${cap}`);
  }

  for (const id of deck.runes) {
    const rune = ctx.index.get(id);
    if (rune.type !== 'rune') problems.push(`${rune.name} is not a rune`);
    if (!fitsIdentity(rune, identity)) {
      problems.push(`rune ${rune.name} is outside the domain identity`);
    }
  }

  // 103.2.d.1. Three signature cards in total, of any type.
  const signatures = deck.main.filter((id) => ctx.index.get(id).superType === 'signature');
  if (signatures.length > 3) problems.push(`${signatures.length} signature cards, cap is 3`);

  return problems;
}
